import { EntityType } from "Model/EntityType";
import { IItem } from "Model/Storage/IItem";
import { IUser } from "Model/Storage/IUser";
import { fastClone, hasNoElements } from "./Array";
import { Configuration } from "./Configuration";
import { dateFromString } from "./Date";
import {
  isAnyDateField, isAnyNumberField, isBoolField, isCurrencyField
} from "./Metadata";
import { getIntlCollator } from "./Intl";
import { isNotNullOrWhiteSpace } from "./String";

export function sortItems(items: IItem[], fieldsToSort: [string, boolean][], locale: string): IItem[] {
  if (hasNoElements(items)) {
    return items;
  }

  return fastClone(items).sort(sorter(fieldsToSort, locale));
}

function sorter(fieldsToSort: [string, boolean][], locale: string): (a: unknown, b: unknown) => number {
  const compareLocaleString = generateLocaleStringCompare(locale);
  const compareLocaleUser = generateLocaleUserCompare(locale);
  return (a: IItem, b: IItem) => fieldsToSort
    .map(([fieldName, descending]) => {
      let compareFn: (x: unknown, y: unknown) => number = compareLocaleString;

      const fieldMetadata = Configuration.getMetadata(fieldName, EntityType.Project);
      if (fieldName === `@users`) {
        compareFn = compareLocaleUser;
      } else if (isAnyDateField(fieldMetadata)) {
        compareFn = compareDateString;
      } else if (isAnyNumberField(fieldMetadata) || isCurrencyField(fieldMetadata)) {
        compareFn = compareNumber;
      } else if (isBoolField(fieldMetadata)) {
        compareFn = compareBoolean;
      }

      return descending ? compareFn(b[fieldName], a[fieldName]) : compareFn(a[fieldName], b[fieldName]);
    })
    .reduce((sortResult, columnSortResult) => (sortResult !== 0 ? sortResult : columnSortResult), 0);
}

function compareNumber(a: number, b: number) {
  const aNullOrUndefined = a == null;
  const bNullOrUndefined = b == null;

  if (aNullOrUndefined && bNullOrUndefined) {
    return 0;
  }

  if (aNullOrUndefined) {
    return -1;
  }

  if (bNullOrUndefined) {
    return 1;
  }

  return a - b;
}

function generateLocaleStringCompare(locale: string) {
  const collator = getIntlCollator(locale);

  return (a: string, b: string) => {
    // required because JS will coerce null → "null" and undefined → "undefined"
    // that leads to unintended results while sorting
    const aValue = isNotNullOrWhiteSpace(a) ? a : ``;
    const bValue = isNotNullOrWhiteSpace(b) ? b : ``;

    return collator.compare(aValue, bValue);
  };
}

function compareDateString(a: string, b: string): number {
  return compareNumber(dateFromString(a)?.valueOf(), dateFromString(b)?.valueOf());
}

export function compareBoolean(a: boolean, b: boolean): number {
  return compareNumber(booleanToNumber(a), booleanToNumber(b));
}

function booleanToNumber(value: boolean): number {
  if (value == null) {
    return null;
  }

  return Number(value);
}

function generateLocaleUserCompare(locale: string): (a: IUser[], b: IUser[]) => number {
  const compare = generateLocaleStringCompare(locale);
  return (a: IUser[], b: IUser[]) => compare(stringifyUserList(a), stringifyUserList(b));
}

function stringifyUserList(userList: IUser[]): string {
  if (hasNoElements(userList)) {
    return ``;
  }

  return userList.map(({ displayName, name }) => displayName ?? name).join(`, `);
}
